import {
    Component,
    Element,
    h,
    Host,
    Listen,
    Prop,
    State,
    Watch,
} from "@stencil/core";

/**
 * This component is a wrapper for comparison matrix tables.
 * One of multiple tables can be chosen from a dropdown.
 *
 * @slot header - The table selection dropdown is placed in this slot
 * @slot (default) - The comparison matrix tables are placed in the default slot
 */
@Component({
    tag: "ux-iws-comparison-matrix",
    styleUrl: "comparison-matrix.scss",
    shadow: false,
})
export class ComparisonMatrix {
    @Element() element: HTMLUxIwsComparisonMatrixElement;

    /**
     * Fixed width of the first table column showing the group title
     */
    @Prop() readonly firstColumnWidth: number = 180;

    @State() activeTableIndex: number = 0;

    private baseClass = "ux-iws-comparison-matrix";

    private updateTables = () => {
        const activeClassName = `${this.baseClass}__table--active`;
        this.element
            .querySelectorAll<HTMLDivElement>(`.${this.baseClass}__table`)
            .forEach((el, index) => {
                if (index !== this.activeTableIndex) {
                    el.classList.remove(activeClassName);
                } else if (!el.classList.contains(activeClassName)) {
                    el.classList.add(activeClassName);
                }
            });
    };

    private getActiveTable = (): HTMLTableElement | undefined => {
        return Array.from(
            this.element.querySelectorAll<HTMLTableElement>("table")
        )[this.activeTableIndex];
    };

    @Listen("uxSelectChange")
    handleSelectTable(event: CustomEvent<string>) {
        const selectElement = (
            event.target as HTMLElement
        ).querySelector<HTMLSelectElement>("select");
        if (selectElement !== null) {
            this.activeTableIndex = selectElement.selectedIndex;
        }
    }

    @Watch("activeTableIndex")
    handleActiveTableIndex() {
        this.updateTables();
        window.requestAnimationFrame(() => {
            const activeTable = this.getActiveTable();
            if (activeTable) {
                activeTable.tabIndex = 0;
                activeTable.focus();
                window.dispatchEvent(new Event("resize"));
            }
        });
    }

    componentWillLoad() {
        this.updateTables();
    }

    render() {
        return (
            <Host
                style={{
                    "--group-column-width": `${this.firstColumnWidth}px`,
                }}
            >
                <div class={`${this.baseClass}__header`}>
                    <slot name="header" />
                </div>
                <div class={`${this.baseClass}__tables`}>
                    <slot />
                </div>
            </Host>
        );
    }
}
